import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export type ToastKind = 'success' | 'error' | 'info'

export interface ToastOptions {
  /** `X-Request-Id` of the failed call: shown as a "Ref: …" line (Frontend §3.5). */
  requestId?: string
  /**
   * A button in the toast (e.g. C-04's "Reload" after a save conflict). A toast with an action stays until the
   * action or Dismiss is clicked, so it can't vanish before the user decides.
   */
  action?: { label: string; onClick: () => void }
}

export type ShowToast = (text: string, kind?: ToastKind, options?: ToastOptions) => void

interface ToastItem {
  id: number
  kind: ToastKind
  text: string
  requestId?: string
  action?: { label: string; onClick: () => void }
}

/** How long a toast stays (the mockup's 3.2 s). */
export const TOAST_DURATION_MS = 3200

const ToastCtx = createContext<ShowToast>(() => undefined)

/**
 * Toasts (mockup `ui.tsx` → `ToastProvider`). Error toasts are `role="alert"`, the others `role="status"`
 * (Frontend §2.4). Each disappears after about 3.2 s.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>())

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const dismiss = useCallback((id: number) => {
    setItems((xs) => xs.filter((x) => x.id !== id))
  }, [])

  const push = useCallback<ShowToast>((text, kind = 'success', options = {}) => {
    const id = nextId.current++
    setItems((xs) => [
      ...xs,
      {
        id,
        kind,
        text,
        ...(options.requestId !== undefined && { requestId: options.requestId }),
        ...(options.action !== undefined && { action: options.action }),
      },
    ])
    if (options.action) return
    const timer = setTimeout(() => {
      timers.current.delete(timer)
      setItems((xs) => xs.filter((x) => x.id !== id))
    }, TOAST_DURATION_MS)
    timers.current.add(timer)
  }, [])

  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
        <div className="toasts">
          {items.map((t) => (
            <div
              key={t.id}
              className={`toast ${t.kind}`}
              role={t.kind === 'error' ? 'alert' : 'status'}
              aria-live={t.kind === 'error' ? 'assertive' : 'polite'}
            >
              {t.kind === 'success' ? (
                <CircleCheck aria-hidden />
              ) : t.kind === 'error' ? (
                <CircleAlert aria-hidden />
              ) : (
                <Info aria-hidden />
              )}
              <span className="toast-text">
                {t.text}
                {t.requestId && <span className="toast-ref">Ref: {t.requestId}</span>}
              </span>
              {t.action && (
                <span className="toast-actions">
                  <button
                    type="button"
                    className="toast-action"
                    onClick={() => {
                      dismiss(t.id)
                      t.action?.onClick()
                    }}
                  >
                    {t.action.label}
                  </button>
                  <button
                    type="button"
                    className="toast-dismiss"
                    aria-label="Dismiss"
                    onClick={() => {
                      dismiss(t.id)
                    }}
                  >
                    <X aria-hidden />
                  </button>
                </span>
              )}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastCtx.Provider>
  )
}

/** `const toast = useToast(); toast('Saved')`, `toast('Something went wrong. Try again.', 'error', { requestId })`. */
export const useToast = (): ShowToast => useContext(ToastCtx)
